// In-viewport selection highlight via OutlineMaterial inverted-hull shells.
// For each selected id we traverse the corresponding Object3D subtree and
// spawn a sibling Mesh with the same geometry + a shared green outline
// material. Shells are tagged `userData.outline = true` so picking ignores
// them. Disposed on selection change.

import * as THREE from 'three';
import { OutlineMaterial } from '../../render/materials/OutlineMaterial';

export class SelectionHighlight {
  private shells = new Map<string, THREE.Mesh[]>();
  private mat: OutlineMaterial;

  constructor() {
    this.mat = new OutlineMaterial(3.5, '#22c55e', 0.55);
  }

  setSelection(ids: string[], objectsById: ReadonlyMap<string, THREE.Object3D>): void {
    const keep = new Set(ids);
    for (const [id, list] of this.shells) {
      if (!keep.has(id)) {
        this.disposeShells(list);
        this.shells.delete(id);
      }
    }
    for (const id of ids) {
      if (this.shells.has(id)) continue;
      const root = objectsById.get(id);
      if (!root) continue;
      const list: THREE.Mesh[] = [];
      this.spawnShellsFor(root, list);
      this.shells.set(id, list);
    }
  }

  /** Re-build shells for a single id (e.g. after async modelRef populates). */
  refresh(id: string, objectsById: ReadonlyMap<string, THREE.Object3D>): void {
    const cur = this.shells.get(id);
    if (cur) this.disposeShells(cur);
    const root = objectsById.get(id);
    if (!root) { this.shells.delete(id); return; }
    const list: THREE.Mesh[] = [];
    this.spawnShellsFor(root, list);
    this.shells.set(id, list);
  }

  clear(): void {
    for (const list of this.shells.values()) this.disposeShells(list);
    this.shells.clear();
  }

  dispose(): void {
    this.clear();
    this.mat.dispose();
  }

  private spawnShellsFor(root: THREE.Object3D, out: THREE.Mesh[]): void {
    root.traverse((obj) => {
      if (!(obj as THREE.Mesh).isMesh) return;
      const mesh = obj as THREE.Mesh;
      if (mesh.userData.outline) return;
      const shell = new THREE.Mesh(mesh.geometry, this.mat);
      shell.userData.outline = true;
      shell.renderOrder = 1;
      shell.castShadow = false;
      shell.receiveShadow = false;
      shell.frustumCulled = mesh.frustumCulled;
      // Match the source mesh's transform by parenting under the same parent
      // and copying local matrix.
      const parent = mesh.parent ?? root;
      shell.position.copy(mesh.position);
      shell.quaternion.copy(mesh.quaternion);
      shell.scale.copy(mesh.scale);
      shell.matrixAutoUpdate = mesh.matrixAutoUpdate;
      parent.add(shell);
      out.push(shell);
      // Bind shell's transform to the mesh on every frame in case the gizmo
      // moves the mesh — we update before render.
      (shell as THREE.Mesh & { __sourceMesh?: THREE.Mesh }).__sourceMesh = mesh;
    });
  }

  /** Sync each shell's transform with its source mesh — call once per frame
   *  before render, so gizmo-driven moves keep the outline aligned. */
  syncTransforms(): void {
    for (const list of this.shells.values()) {
      for (const shell of list) {
        const src = (shell as THREE.Mesh & { __sourceMesh?: THREE.Mesh }).__sourceMesh;
        if (!src) continue;
        shell.position.copy(src.position);
        shell.quaternion.copy(src.quaternion);
        shell.scale.copy(src.scale);
      }
    }
  }

  private disposeShells(list: THREE.Mesh[]): void {
    for (const shell of list) {
      shell.parent?.remove(shell);
    }
  }
}
